"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  number: "01",
  label: "// ACQUISITION",
  title: "DATA ACQUISITION",
  duration: "~30MIN",
  description: "We secure the digital evidence chain. Every transaction hash, wallet address, and metadata point is cryptographically verified and logged in our immutable forensic vault.",
  bullets: ["Blockchain node synchronization", "Transaction path mapping", "Wallet signature verification", "Metadata integrity logging"],
  offset: "md:ml-0"
}, {
  number: "02",
  label: "// ANALYSIS",
  title: "PATTERN ANALYSIS",
  duration: "~2HRS",
  description: "Our proprietary heuristic engines scan the ledger for anomalies. We isolate obfuscation techniques, mixers, and cross-chain bridges to identify the source of the breach.",
  bullets: ["Heuristic anomaly detection", "Mixer obfuscation bypass", "Cross-chain bridge tracking", "Entity attribution modeling"],
  offset: "md:ml-8"
}, {
  number: "03",
  label: "// REPORTING",
  title: "INTELLIGENCE REPORT",
  duration: "~24HRS",
  description: "A comprehensive forensic dossier is generated. We provide actionable intelligence, asset recovery pathways, and legal-ready documentation for regulatory authorities.",
  bullets: ["Asset recovery roadmap", "Regulatory compliance filing", "Forensic evidence dossier", "Risk mitigation strategy"],
  offset: "md:ml-4"
}, {
  number: "04",
  label: "// MITIGATION",
  title: "THREAT MITIGATION",
  duration: "~6HRS",
  description: "We implement defensive protocols to prevent further exposure. Smart contract audits and wallet security hardening ensure your digital assets remain under your control.",
  bullets: ["Smart contract vulnerability audit", "Wallet security hardening", "Access control remediation", "Real-time threat monitoring"],
  offset: "md:ml-12"
}, {
  number: "05",
  label: "// VERIFICATION",
  title: "FINAL VERIFICATION",
  duration: "~1HR",
  description: "The ledger is audited one final time to confirm asset security. Our senior analysts sign off on the integrity of the environment before closing the case file.",
  bullets: ["Final ledger integrity audit", "Security posture validation", "Asset movement monitoring", "Senior analyst sign-off"],
  offset: "md:ml-2"
}];
export default function TheInvestigativeProcess() {
  const header = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="4441" id="the-investigative-process" className="bg-background text-foreground relative overflow-hidden py-32 px-4 sm:px-6 lg:px-12">
      <div className="pointer-events-none absolute top-8 right-8 font-mono text-[10px] tracking-widest bg-foreground text-background px-3 py-1 rounded-lg">
        UTC <span className="text-destructive">00:00</span> // GLOBAL_LEDGER
      </div>
      <div className="pointer-events-none absolute top-8 left-8 font-mono text-[10px] tracking-widest text-muted-foreground">
        [SYS_02] // INVESTIGATIVE_SEQUENCE
      </div>

      <div className="mx-auto max-w-7xl">
        <div ref={header.ref} className={`mb-24 transition-all duration-700 ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="flex items-center gap-4 mb-6">
            {}
          </div>

          <h2 className="font-default font-semibold uppercase leading-none tracking-tighter text-foreground text-5xl md:text-7xl lg:text-8xl mb-12">
            THE <span className="text-primary">/</span> PROCESS
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-7">
              <p className="font-default text-lg md:text-xl leading-relaxed text-muted-foreground max-w-2xl">
                Every investigation follows a rigorous, multi-stage forensic sequence. 
                We utilize advanced crypto scam database analysis to ensure absolute 
                transparency and security for your digital assets.
              </p>
            </div>
            <div className="md:col-span-5 flex justify-end">
              <div className="font-mono text-[10px] text-muted-foreground border border-border p-4 rounded-lg bg-card/30 backdrop-blur-sm">
                <div className="flex justify-between gap-8 mb-2">
                  <span>LAT: 40.7128° N</span>
                  <span>LNG: 74.0060° W</span>
                </div>
                <div className="text-primary">STATUS: ACTIVE_INVESTIGATION</div>
              </div>
            </div>
          </div>
        </div>

        <div className="relative border-t border-border/50 pt-16">
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-border/30 -translate-x-1/2 hidden md:block">
            <div className="absolute inset-x-0 h-40 bg-gradient-to-b from-transparent via-primary to-transparent animate-[scan_4s_linear_infinite]" />
          </div>

          <style>{`
            @keyframes scan {
              0% { transform: translateY(-100%); }
              100% { transform: translateY(1000%); }
            }
          `}</style>

          <div className="space-y-24 relative">
            {steps.map((step, i) => <TimelineStep key={i} step={step} i={i} />)}
          </div>
        </div>
      </div>
    </section>;
}
function TimelineStep({
  step,
  i
}: {
  step: any;
  i: number;
}) {
  const reveal = useRevealOnScroll<HTMLDivElement>();
  const isEven = i % 2 === 0;
  return <div data-index={i} ref={reveal.ref} className={`relative grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-24 transition-all duration-1000 ${reveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16"}`}>
      {}
      <div className={`hidden md:flex flex-col justify-center ${isEven ? "order-1 items-end text-right" : "order-2 items-start text-left"}`}>
        <div className="font-mono text-[10px] text-primary mb-4 tracking-[0.3em]">
          [{step.number}] // {step.label.replace("// ", "")}
        </div>
        <h3 className="font-default font-semibold uppercase text-4xl lg:text-5xl tracking-tighter text-foreground mb-6 leading-none">
          {step.title}
        </h3>
        <div className="font-mono text-xs text-muted-foreground border border-border px-3 py-1 rounded-lg">
          EST_TIME: {step.duration}
        </div>
      </div>

      <div className={`relative order-1 md:${isEven ? "order-2" : "order-1"}`}>
        {}
        <div className="absolute left-0 md:left-1/2 top-1/2 -translate-y-1/2 -translate-x-1/2 z-20 hidden md:block">
          <div className="size-4 bg-background border-2 border-primary rounded-lg flex items-center justify-center">
            <div className="size-1.5 bg-primary rounded-lg" />
          </div>
        </div>

        <div className="relative bg-card border border-border p-8 md:p-10 rounded-lg shadow-xl overflow-hidden group">
          {}
          <div className="absolute top-2 left-2 font-mono text-[8px] text-muted-foreground/30">+</div>
          <div className="absolute top-2 right-2 font-mono text-[8px] text-muted-foreground/30">+</div>
          <div className="absolute bottom-2 left-2 font-mono text-[8px] text-muted-foreground/30">+</div>
          <div className="absolute bottom-2 right-2 font-mono text-[8px] text-muted-foreground/30">+</div>

          {}
          <div className="md:hidden mb-6">
            <div className="font-mono text-[10px] text-primary mb-2">[{step.number}]</div>
            <h3 className="font-default font-semibold uppercase text-3xl tracking-tighter text-foreground mb-2">
              {step.title}
            </h3>
            <div className="font-mono text-[10px] text-muted-foreground">{step.duration}</div>
          </div>

          <p className="font-default text-base leading-relaxed text-muted-foreground mb-8">
            {step.description}
          </p>

          <div className="grid grid-cols-1 gap-y-3">
            {step.bullets.map((bullet: string, bi: number) => <div data-index={bi} key={bi} className="flex items-center gap-3 font-mono text-[10px] tracking-wider text-foreground/70 uppercase border-b border-border/30 pb-2 last:border-0">
                <span className="text-primary">0{bi + 1}</span>
                <span>{bullet}</span>
              </div>)}
          </div>
          
          {}
          <div className="absolute inset-x-0 top-0 h-px bg-primary/50 scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
        </div>
      </div>
    </div>;
}